"use client";

import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

export function digitsOnly(phone: string) {
  return String(phone || "").replace(/\D/g, "");
}

/** Build a dialable tel: href from any phone display string. */
export function telHref(phone: string) {
  const digits = digitsOnly(phone);
  if (digits.length < 8) return "";
  if (digits.length === 10) return `tel:+91${digits}`;
  if (digits.length === 12 && digits.startsWith("91")) return `tel:+${digits}`;
  if (digits.startsWith("0") && digits.length === 11) return `tel:+91${digits.slice(1)}`;
  return `tel:+${digits}`;
}

/** Country-coded digits for WhatsApp, e.g. 919324261020 */
export function waPhoneDigits(phone: string) {
  const digits = digitsOnly(phone);
  if (digits.length < 8) return "";
  if (digits.length === 10) return `91${digits}`;
  if (digits.length === 11 && digits.startsWith("0")) return `91${digits.slice(1)}`;
  if (digits.length === 12 && digits.startsWith("91")) return digits;
  if (digits.length > 10) return `91${digits.slice(-10)}`;
  return digits;
}

export function waHref(phone: string) {
  const national = waPhoneDigits(phone);
  return national ? `https://wa.me/${national}` : "";
}

export function formatInPhone(phone: string) {
  const digits = digitsOnly(phone);
  if (digits.length === 10) return `+91 ${digits}`;
  if (digits.length === 12 && digits.startsWith("91")) return `+91 ${digits.slice(2)}`;
  return String(phone || "").trim();
}

function isAndroidDevice() {
  if (typeof navigator === "undefined") return false;
  return /Android/i.test(navigator.userAgent || "");
}

/** Open a specific WhatsApp app on Android; https fallback elsewhere. */
function openWhatsAppChoice(phone: string, app: "personal" | "business") {
  const national = waPhoneDigits(phone);
  if (!national) return;
  const web = `https://wa.me/${national}`;

  if (!isAndroidDevice()) {
    window.open(web, "_blank", "noopener,noreferrer");
    return;
  }

  const pkg = app === "business" ? "com.whatsapp.w4b" : "com.whatsapp";
  const intent = `intent://send/?phone=${national}#Intent;scheme=whatsapp;package=${pkg};S.browser_fallback_url=${encodeURIComponent(web)};end`;
  window.location.href = intent;
}

type PhoneLinkProps = {
  phone?: string | null;
  children?: ReactNode;
  className?: string;
  fallback?: ReactNode;
};

type MenuPos = { top: number; left: number };

export default function PhoneLink({ phone, children, className, fallback = "N/A" }: PhoneLinkProps) {
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [android, setAndroid] = useState(false);
  const [menuPos, setMenuPos] = useState<MenuPos>({ top: 0, left: 0 });
  const rootRef = useRef<HTMLSpanElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const call = telHref(phone || "");
  const whatsapp = waHref(phone || "");
  const label = children ?? formatInPhone(phone || "") ?? phone;

  useEffect(() => {
    setMounted(true);
    setAndroid(isAndroidDevice());
  }, []);

  useLayoutEffect(() => {
    if (!open || !rootRef.current) return;

    function placeMenu() {
      const trigger = rootRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const menuWidth = menuRef.current?.offsetWidth || 180;
      const menuHeight = menuRef.current?.offsetHeight || 140;
      const gap = 8;
      let left = rect.left + rect.width / 2 - menuWidth / 2;
      left = Math.max(8, Math.min(left, window.innerWidth - menuWidth - 8));
      let top = rect.bottom + gap;
      if (top + menuHeight > window.innerHeight - 8 && rect.top > menuHeight + gap) {
        top = rect.top - menuHeight - gap;
      }
      setMenuPos({ top, left });
    }

    placeMenu();
    window.addEventListener("resize", placeMenu);
    window.addEventListener("scroll", placeMenu, true);
    return () => {
      window.removeEventListener("resize", placeMenu);
      window.removeEventListener("scroll", placeMenu, true);
    };
  }, [open, android]);

  useEffect(() => {
    if (!open) return;
    function onPointer(event: PointerEvent) {
      const target = event.target as Node;
      if (rootRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!call && !whatsapp) return <span className={className}>{fallback}</span>;

  function toggleMenu(event: MouseEvent | KeyboardEvent) {
    event.preventDefault();
    event.stopPropagation();
    setOpen((current) => !current);
  }

  const menu =
    open && mounted
      ? createPortal(
          <div
            ref={menuRef}
            className="phone-action-menu"
            role="menu"
            style={{ top: menuPos.top, left: menuPos.left }}
          >
            {call ? (
              <a role="menuitem" href={call} onClick={() => setOpen(false)}>
                Call
              </a>
            ) : null}
            {whatsapp ? (
              android ? (
                <>
                  <button
                    type="button"
                    role="menuitem"
                    className="phone-action-item"
                    onClick={() => {
                      setOpen(false);
                      openWhatsAppChoice(phone || "", "personal");
                    }}
                  >
                    WhatsApp
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className="phone-action-item"
                    onClick={() => {
                      setOpen(false);
                      openWhatsAppChoice(phone || "", "business");
                    }}
                  >
                    WhatsApp Business
                  </button>
                </>
              ) : (
                <a
                  role="menuitem"
                  href={whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setOpen(false)}
                >
                  WhatsApp
                </a>
              )
            ) : null}
          </div>,
          document.body
        )
      : null;

  return (
    <span
      ref={rootRef}
      className={`phone-action${open ? " is-open" : ""}${className ? ` ${className}` : ""}`}
    >
      <span
        role="button"
        tabIndex={0}
        className="phone-action-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={toggleMenu}
        onKeyDown={(event) => {
          if (event.key !== "Enter" && event.key !== " ") return;
          toggleMenu(event);
        }}
      >
        {label}
      </span>
      {menu}
    </span>
  );
}
